import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    Chip,
    Separator,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NameOption } from '../../nameOptions';
import type { NameKind } from '../../namesDecisions';
import type { NameSectionModel, PickerKey } from '../../useNamesStep';
import { NameRows } from './NameRows';

type NameSectionProps = {
    section: NameSectionModel;
    existing: NameOption[];
    parents: NameOption[];
    isCatalogLoading: boolean;
    /** Categories: rows with an empty Category cell, which stay Unsorted. */
    unsortedRows?: number;
    onAcceptAll: (kind: NameKind) => void;
    onActionChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onTargetChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onNameChange: (kind: NameKind, text: string, name: string) => void;
    onParentChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onAddAxesChange: (kind: NameKind, text: string, addAxes: boolean) => void;
};

// Each section is a second-level heading under the step's title.
const asH2: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h2 {...domProps}>{children}</h2>;

// The group of existing categories is a third-level heading inside its section.
const asH3: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h3 {...domProps}>{children}</h3>;

// Literal keys per kind, so the locale guards see every one of them.
const TITLE_KEY: Record<NameKind, string> = {
    brands: 'catalogImport.names.sections.brands.title',
    categories: 'catalogImport.names.sections.categories.title',
    suppliers: 'catalogImport.names.sections.suppliers.title',
};

const EMPTY_KEY: Record<NameKind, string> = {
    brands: 'catalogImport.names.sections.brands.empty',
    categories: 'catalogImport.names.sections.categories.empty',
    suppliers: 'catalogImport.names.sections.suppliers.empty',
};

const ACCEPT_ALL_ARIA_KEY: Record<NameKind, string> = {
    brands: 'catalogImport.names.sections.brands.acceptAllAria',
    categories: 'catalogImport.names.sections.categories.acceptAllAria',
    suppliers: 'catalogImport.names.sections.suppliers.acceptAllAria',
};

/**
 * One kind of name (brands, categories or suppliers): the title with its
 * count (and, for suppliers, how many cells list several of them), Accept
 * all (for the new names; it has nothing to do when the section lists none),
 * and a row per name; a line when the file has no new one. The categories
 * section then lists the existing categories that gain options, under their
 * own heading, and ends with the rows that stay Unsorted.
 */
export const NameSection: React.FC<NameSectionProps> = ({
    section,
    existing,
    parents,
    isCatalogLoading,
    unsortedRows = 0,
    onAcceptAll,
    onActionChange,
    onTargetChange,
    onNameChange,
    onParentChange,
    onAddAxesChange,
}) => {
    const { t } = useTranslation('pages');
    const { kind, rows, gainingOptions, joinedRows } = section;
    const rowProps = {
        actions: section.actions,
        existing,
        isCatalogLoading,
        kind,
        onActionChange,
        onAddAxesChange,
        onNameChange,
        onParentChange,
        onTargetChange,
        parents,
    };

    return (
        <Card
            className="gap-0 p-0"
            {...getNodeTestIdProp(`catalog-import-names-section-${kind}`)}
        >
            <Card.Header className="flex-row items-center justify-between gap-3 px-4 py-4 sm:px-6">
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                    <Card.Title render={asH2}>
                        {t(TITLE_KEY[kind], { total: countParam(rows.length) })}
                    </Card.Title>
                    {joinedRows > 0 ? (
                        <Chip
                            color="accent"
                            size="sm"
                            variant="soft"
                            {...getNodeTestIdProp(
                                'catalog-import-names-joined-count',
                            )}
                        >
                            {t('catalogImport.names.joinedCount', {
                                count: joinedRows,
                            })}
                        </Chip>
                    ) : null}
                </div>
                <Button
                    aria-label={t(ACCEPT_ALL_ARIA_KEY[kind])}
                    isDisabled={rows.every((row) => row.match !== 'new')}
                    onPress={() => onAcceptAll(kind)}
                    size="sm"
                    variant="ghost"
                    {...getNodeTestIdProp('catalog-import-names-accept-all')}
                >
                    {t('catalogImport.names.acceptAll')}
                </Button>
            </Card.Header>
            <Separator />
            {rows.length === 0 ? (
                <Typography
                    className="px-4 py-4 sm:px-6"
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp('catalog-import-names-section-empty')}
                >
                    {t(EMPTY_KEY[kind])}
                </Typography>
            ) : (
                <NameRows {...rowProps} rows={rows} />
            )}
            {gainingOptions.length > 0 ? (
                <div
                    className="flex flex-col"
                    {...getNodeTestIdProp(
                        'catalog-import-names-gaining-options',
                    )}
                >
                    <Separator />
                    <div className="flex flex-col gap-1 px-4 pt-4 pb-1 sm:px-6">
                        <Typography render={asH3} type="h6">
                            {t(
                                'catalogImport.names.sections.categoriesGainingOptions.title',
                                { total: countParam(gainingOptions.length) },
                            )}
                        </Typography>
                        <Typography color="muted" type="body-sm">
                            {t(
                                'catalogImport.names.sections.categoriesGainingOptions.description',
                            )}
                        </Typography>
                    </div>
                    <NameRows {...rowProps} rows={gainingOptions} />
                </div>
            ) : null}
            {unsortedRows > 0 ? (
                <>
                    <Separator />
                    <Typography
                        className="px-4 py-3 sm:px-6"
                        color="muted"
                        type="body-xs"
                        {...getNodeTestIdProp('catalog-import-names-unsorted')}
                    >
                        {t('catalogImport.names.unsorted', {
                            count: unsortedRows,
                            rows: countParam(unsortedRows),
                        })}
                    </Typography>
                </>
            ) : null}
        </Card>
    );
};
